import { apiClient as baseApiClient } from '#/api/client.tsx';
import type {
  CompulsoryInsurancePenaltyTicket,
  CompulsoryInsurancePenaltyTicketOperatorNote,
  CompulsoryInsurancePenaltyTicketState,
  CompulsoryInsurancePenaltyTicketSummary,
} from '../model';
import { format } from 'date-fns';

const cipApiClient = baseApiClient.create({
  baseURL: `${import.meta.env.VITE_BASE_URL}/cockpit/auto-pay`,
});

const TICKETS_BASE = '/compulsory-insurance-penalty-auto-pay-tickets';

type ISODateTime = string;

type ListTicketsParams = {
  page?: number;
  pageSize?: number;
  state?: CompulsoryInsurancePenaltyTicketState[];
  accountEmail?: string;
  plateNumber?: string;
  createDateTimeFrom?: ISODateTime;
  createDateTimeTo?: ISODateTime;
};

type CompleteTicketBody = {
  completeDateTime: ISODateTime;
};

// --- Response types ---

type ChargeTicketResponse = {
  id: string;
  state: string;
  paymentId: string | null;
};

type TicketStateChangeResponse = Pick<
  CompulsoryInsurancePenaltyTicket,
  'id' | 'state'
>;

// --- Endpoints ---

type ListTicketsResponse = {
  data: CompulsoryInsurancePenaltyTicketSummary[];
  extra: {
    totalElement: number;
    totalPage: number;
  };
};

// === info getter ===
export const listTickets = (
  params: ListTicketsParams = {} as ListTicketsParams,
) => {
  return cipApiClient
    .get<ListTicketsResponse>(`${TICKETS_BASE}`, {
      params,
      paramsSerializer(params) {
        const cleaned = Object.fromEntries(
          Object.entries(params).filter(([, value]) => value !== undefined),
        );

        const urlParams = new URLSearchParams(cleaned);
        return urlParams.toString();
      },
    })
    .then((res) => res.data);
};

export const getTicketById = (id: string) => {
  return cipApiClient
    .get<{ data: CompulsoryInsurancePenaltyTicket }>(`${TICKETS_BASE}/${id}`)
    .then((res) => res.data.data);
};

type ExportTicketsParams = {
  from?: ISODateTime;
  to?: ISODateTime;
};

type ExportCompletedTicketParams = Required<ExportTicketsParams>;

export const exportCompletedTicket = (params: ExportCompletedTicketParams) => {
  return cipApiClient
    .get<Blob>(`${TICKETS_BASE}/excel`, {
      params: { state: 'COMPLETED', ...params },
      responseType: 'blob',
    })
    .then((res) => {
      const currentTimestamp = format(new Date(), 'HHmmss');
      const startDate = format(new Date(params.from), 'yyyyMMdd');
      const endDate = format(new Date(params.to), 'yyyyMMdd');
      const filename = `違反強制險罰鍰_${startDate}-${endDate}_${currentTimestamp}`;
      return {
        blob: res.data,
        filename,
      };
    });
};

// === finish the query of the ticket ===

type FailedMutationBody = {
  reason: string;
};

export const markNoFee = (id: string, body: FailedMutationBody) =>
  cipApiClient
    .post<{
      data: TicketStateChangeResponse;
    }>(`${TICKETS_BASE}/${id}/no-fee`, body)
    .then((res) => res.data.data);

export const markQueryFailed = (id: string, body: FailedMutationBody) =>
  cipApiClient
    .post<{
      data: TicketStateChangeResponse;
    }>(`${TICKETS_BASE}/${id}/query-failed`, body)
    .then((res) => res.data.data);

type ChargeTicketBody = {
  amount: number;
  handlingFee: number;
};

export const chargeTicket = (id: string, body: ChargeTicketBody) =>
  cipApiClient
    .post<{
      data: ChargeTicketResponse;
    }>(`${TICKETS_BASE}/${id}/charge`, body)
    .then((res) => res.data.data);

export const completeTicket = (id: string, body: CompleteTicketBody) =>
  cipApiClient
    .post<{
      data: TicketStateChangeResponse;
    }>(`${TICKETS_BASE}/${id}/complete`, body)
    .then((res) => res.data.data);

type AppendOperatorNoteBody = Pick<
  CompulsoryInsurancePenaltyTicketOperatorNote,
  'content'
>;

type AddOperatorNoteResponse = CompulsoryInsurancePenaltyTicketOperatorNote;

export const appendOperatorNote = (id: string, body: AppendOperatorNoteBody) =>
  cipApiClient
    .post<{
      data: AddOperatorNoteResponse;
    }>(`${TICKETS_BASE}/${id}/operator-notes`, body)
    .then((res) => res.data.data);

export type {
  ListTicketsParams,
  ExportTicketsParams,
  ExportCompletedTicketParams,
  ChargeTicketBody,
  CompleteTicketBody,
  AppendOperatorNoteBody as AddOperatorNoteBody,
  ListTicketsResponse,
  ChargeTicketResponse,
  TicketStateChangeResponse,
  AddOperatorNoteResponse,
};
